import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Archive, Eye, Search, ChevronDown, ChevronUp, Calendar, Clock,
  User, Building, AlertTriangle
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/employee-archives`;

const UsersArchive = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('-archived_at');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchArchives();
  }, [searchTerm, sortBy, currentPage, pageSize]);

  const fetchArchives = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams({
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
      });
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(`${API_BASE_URL}/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setArchives(response.data.results);
      setTotalItems(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      console.error('Error fetching archives:', err);
      setError('Failed to fetch archives. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy(field);
    } else {
      setSortBy(field);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field && sortBy !== `-${field}`) {
      return <ChevronDown className="w-4 h-4 text-slate-400" />;
    }
    return sortBy.startsWith('-')
      ? <ChevronDown className="w-4 h-4 text-brand-600" />
      : <ChevronUp className="w-4 h-4 text-brand-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getDaysLeftColor = (daysLeft) => {
    if (daysLeft <= 30) return 'text-red-600 bg-red-50';
    if (daysLeft <= 90) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  const getVisiblePages = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-shrink-0 p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Archive className="w-8 h-8 text-brand-600" />
            <h1 className="text-3xl font-bold text-slate-900">Employee Archives</h1>
          </div>
        </div>
        <p className="text-slate-600 mt-2">
          View archived employee data. Archives are retained for 2 years after employee departure.
        </p>
      </div>

      <div className="flex-1 px-4 pb-4 overflow-hidden">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-100 h-full flex flex-col">
          <div className="p-4 flex-shrink-0">
            <div className="mb-2 flex space-x-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search archives..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                />
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                {error}
              </div>
            )}
          </div>

          {/* Table container */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                </div>
              ) : archives.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <Archive className="w-16 h-16 mb-4 text-slate-300" />
                  <p className="text-lg">No archives found</p>
                  <p className="text-sm">Archives will appear here when employees are archived.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-100 border-b-2 border-brand-500 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Employee
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('archived_at')}
                      >
                        <div className="flex items-center gap-2">
                          Archived Date
                          <SortIcon field="archived_at" />
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('expires_at')}
                      >
                        <div className="flex items-center gap-2">
                          Expires
                          <SortIcon field="expires_at" />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Days Left
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Archived By
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Notification
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-200">
                    {archives.map((archive) => (
                      <tr
                        key={archive.id}
                        className="hover:bg-brand-50 transition-colors cursor-pointer"
                        onDoubleClick={() => navigate(`/users/archives/${archive.id}`)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="bg-slate-100 rounded-full p-2">
                              <User className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">
                                {archive.employee_name}
                              </div>
                              <div className="text-sm text-slate-500">
                                {archive.notification_email || 'No email'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Calendar className="w-4 h-4" />
                            {formatDate(archive.archived_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="w-4 h-4" />
                            {formatDate(archive.expires_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-sm font-medium ${getDaysLeftColor(archive.days_until_expiry)}`}>
                            {archive.is_expired ? 'Expired' : `${archive.days_until_expiry} days`}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">
                          {archive.archived_by_name || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {archive.notification_sent ? (
                            <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              Sent
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-400 text-sm">
                              <span className="w-2 h-2 bg-slate-300 rounded-full"></span>
                              Not sent
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => navigate(`/users/archives/${archive.id}`)}
                            className="text-brand-600 hover:text-brand-700 p-2 rounded-lg hover:bg-brand-50 transition-colors"
                            title="View archive details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          {!loading && archives.length > 0 && (
            <div className="flex items-center justify-between mt-4 px-4 pb-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-600">
                  Showing {(currentPage - 1) * pageSize + 1}-
                  {Math.min(currentPage * pageSize, totalItems)} of {totalItems} items
                </div>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="text-sm px-4 py-2 border border-steel-300 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              <div className="flex space-x-2">
                {currentPage > 1 && (
                  <button
                    className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-brand-50 transition-colors"
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                )}
                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    className={`px-4 py-2 border ${
                      page === currentPage
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-steel-300 text-slate-700'
                    } rounded-xl hover:bg-brand-50 transition-colors`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                {currentPage < totalPages && (
                  <button
                    className="px-4 py-2 border border-steel-300 text-slate-700 rounded-xl hover:bg-brand-50 transition-colors"
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersArchive;
